import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error(
    'Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY.\n' +
    'Create frontend/.env with:\n' +
    '  VITE_SUPABASE_URL=https://<project>.supabase.co\n' +
    '  VITE_SUPABASE_ANON_KEY=<anon-key>'
  );
}

function makeTimeoutFetch(ms: number) {
  return (url: RequestInfo | URL, options?: RequestInit): Promise<Response> => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ms);
    return fetch(url, { ...options, signal: controller.signal })
      .finally(() => clearTimeout(timer));
  };
}

// Refresh token cookie helpers.
// We store ONLY the refresh_token (~50 chars) in a cookie — not the full session JSON.
// Cookies are shared between Safari and home-screen PWA on iOS (localStorage is not),
// so this lets the PWA recover the session that was established in Safari.
const RT_COOKIE = 'hu_rt';

export function setRefreshTokenCookie(token: string) {
  try {
    const maxAge = 30 * 24 * 60 * 60; // 30 days
    document.cookie = `${RT_COOKIE}=${token}; max-age=${maxAge}; path=/; SameSite=Lax`;
  } catch { /* ignore */ }
}

export function getRefreshTokenCookie(): string | null {
  try {
    const match = document.cookie.match(/(?:^|; )hu_rt=([^;]*)/);
    return match ? match[1] : null;
  } catch { return null; }
}

export function deleteRefreshTokenCookie() {
  try {
    document.cookie = `${RT_COOKIE}=; max-age=0; path=/`;
  } catch { /* ignore */ }
}

// Data client — always anon, never uses auth tokens
// Used for all CRUD operations (users, tournaments, orders, etc.)
export const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '', {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
    storageKey: 'sb-data-noop',
  },
  global: { fetch: makeTimeoutFetch(30000) },
});

// Auth client — only for email OTP login (signInWithOtp, verifyOtp, getSession)
// Uses default localStorage for the full session + a small cookie for the refresh token.
export const supabaseAuth = createClient(supabaseUrl || '', supabaseAnonKey || '', {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    // iOS PWA/Safari can deadlock on the default navigator.locks-based lock,
    // hanging getSession() forever (app stuck on "checking_session"). We run
    // single-context, so a pass-through lock is safe and avoids the hang.
    lock: async (_name: string, _acquireTimeout: number, fn: () => Promise<any>) => fn(),
  },
  global: { fetch: makeTimeoutFetch(30000) },
});

// Keep the refresh token cookie in sync with the auth state.
// This runs once when the module is loaded.
supabaseAuth.auth.onAuthStateChange((event, session) => {
  if (session?.refresh_token) {
    setRefreshTokenCookie(session.refresh_token);
  } else if (event === 'SIGNED_OUT') {
    deleteRefreshTokenCookie();
  }
});
